import { type ChangeEvent, memo, type ReactNode, type Ref, type RefObject, useCallback, useRef } from 'react';
import { useIsomorphicLayoutEffect } from 'react-use';

import { Input } from '@air/primitive-input';
import { Select, SelectContent, SelectItem, SelectTrigger } from '@air/primitive-select';
import { cn } from '@air/utils-tailwind';

import { useWorkspaceAgentGuidedVariantOutputOptionsDockHotkeys } from '~/components/v2/components/workspace/workspace-agent-panel/components/composer/guided-variants/useWorkspaceAgentGuidedVariantOutputOptionsDockHotkeys';
import { workspaceAgentConsentDockShell } from '~/components/v2/components/workspace/workspace-agent-panel/constants/workspaceAgentComposerChrome';
import { FIELD_VALUE_MAX_LENGTH } from '~/components/v2/components/workspace/workspace-agent-panel/constants/workspaceAgentFormInput';
import { WorkspaceAgentDockActions } from '~/components/v2/components/workspace/workspace-agent-panel/design-system/consent/WorkspaceAgentDockActions';
import { WorkspaceAgentDockFrame } from '~/components/v2/components/workspace/workspace-agent-panel/design-system/consent/WorkspaceAgentDockFrame';
import { WorkspaceAgentFormField } from '~/components/v2/components/workspace/workspace-agent-panel/design-system/forms/WorkspaceAgentFormField';
import {
  OUTPUT_FORMAT_LABEL,
  type WorkspaceAgentGuidedVariantOutput,
  type WorkspaceAgentGuidedVariantOutputFormat,
} from '~/components/v2/components/workspace/workspace-agent-panel/utils/workspaceAgentGuidedVariantOutput';

export type WorkspaceAgentGuidedVariantOutputOptionsDockProps = {
  allowsFormat: boolean;
  draft: WorkspaceAgentGuidedVariantOutput;
  formats: readonly WorkspaceAgentGuidedVariantOutputFormat[];
  onCancel: () => void;
  onContinue: () => void;
  onFormatChange: (value: string) => void;
  onSuffixChange: (event: ChangeEvent<HTMLInputElement>) => void;
  panelBody: HTMLElement | null;
  suffixInputRef: Ref<HTMLInputElement>;
  title: string;
  titleId: string;
  triggerRef: RefObject<HTMLButtonElement | null>;
};

export const WorkspaceAgentGuidedVariantOutputOptionsDock = memo(
  ({
    allowsFormat,
    draft,
    formats,
    onCancel,
    onContinue,
    onFormatChange,
    onSuffixChange,
    panelBody,
    suffixInputRef,
    title,
    titleId,
    triggerRef,
  }: WorkspaceAgentGuidedVariantOutputOptionsDockProps) => {
    const sectionRef = useRef<HTMLElement | null>(null);
    const { dockRef } = useWorkspaceAgentGuidedVariantOutputOptionsDockHotkeys({ onCancel, onContinue });
    const setDockRef = useCallback(
      (node: HTMLElement | null) => {
        sectionRef.current = node;
        dockRef.current = node;
      },
      [dockRef],
    );

    /*
     * Layout cleanup, not passive: by the time a passive cleanup runs React has already removed this
     * subtree and focus has fallen to document.body. Return focus while this node still owns it.
     */
    useIsomorphicLayoutEffect(() => {
      const dock = sectionRef.current;

      return () => {
        if (dock?.contains(document.activeElement)) {
          triggerRef.current?.focus({ preventScroll: true });
        }
      };
    }, [triggerRef]);

    const renderSuffixInput = useCallback(
      (controlId: string): ReactNode => (
        <Input
          autoComplete="off"
          className="w-full rounded-8"
          data-dock-continue-on-enter
          data-testid="WORKSPACE_AGENT_GUIDED_VARIANT_OUTPUT_SUFFIX"
          id={controlId}
          maxLength={FIELD_VALUE_MAX_LENGTH}
          onChange={onSuffixChange}
          placeholder="Optional, e.g. _web"
          ref={suffixInputRef}
          size="large"
          spellCheck={false}
          value={draft.suffix}
        />
      ),
      [draft.suffix, onSuffixChange, suffixInputRef],
    );

    const renderFormatSelect = useCallback(
      (controlId: string): ReactNode => (
        <Select onValueChange={onFormatChange} value={draft.format}>
          <SelectTrigger
            className="w-full"
            data-testid="WORKSPACE_AGENT_GUIDED_VARIANT_OUTPUT_FORMAT"
            id={controlId}
            size="large"
          />
          <SelectContent>
            {formats.map((format) => (
              <SelectItem data-format={format} key={format} value={format}>
                {OUTPUT_FORMAT_LABEL[format]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      ),
      [draft.format, formats, onFormatChange],
    );

    return (
      <section
        aria-labelledby={titleId}
        className={cn(workspaceAgentConsentDockShell(), panelBody && 'absolute inset-x-0 bottom-0 z-10 flex-none')}
        data-testid="WORKSPACE_AGENT_GUIDED_VARIANT_OUTPUT_DOCK"
        ref={setDockRef}
      >
        <WorkspaceAgentDockFrame title={title} titleId={titleId}>
          <div className="flex flex-col gap-3">
            <WorkspaceAgentFormField data-testid="WORKSPACE_AGENT_GUIDED_VARIANT_OUTPUT_SUFFIX_FIELD" label="Suffix">
              {renderSuffixInput}
            </WorkspaceAgentFormField>
            {allowsFormat ? (
              <WorkspaceAgentFormField data-testid="WORKSPACE_AGENT_GUIDED_VARIANT_OUTPUT_FORMAT_FIELD" label="Format">
                {renderFormatSelect}
              </WorkspaceAgentFormField>
            ) : null}
          </div>
        </WorkspaceAgentDockFrame>
        <WorkspaceAgentDockActions approveLabel="Continue" onApprove={onContinue} onCancel={onCancel} />
      </section>
    );
  },
);

WorkspaceAgentGuidedVariantOutputOptionsDock.displayName = 'WorkspaceAgentGuidedVariantOutputOptionsDock';
